import { element as getElement, globalEvent } from '@shared/easyfy.core';
import { invoke } from '@shared/easyfy.decorators';
import { getDialogManager } from '@webModules/DialogManager';

namespace Easyfy.Modules {
  @invoke
  export class ProductCard {

    private sponsoredDialogHead: string;
    private sponsoredInfoText: string;
    private sponsoredReadMoreButtonText: string;
    private sponsoredReadMoreButtonLink: string;
    private sponsoredProductButtonText: string;
    private sponsoredProductButtonLink: string;

    onInit() {

      document.addEventListener("click", (event: Event) => {
        const target = event.target as HTMLElement;

        // Skip if the target is a button.
        if (target.tagName.toUpperCase() === 'BUTTON') {
          return;
        }

        // Check for show-sponsor-info action first (more specific),
        const sponsorInfo = target.closest<HTMLElement>('[data-action="show-sponsor-info"]');
        if (sponsorInfo) {
          this.onShowSponsorInfoDialog(event);
          return; // Stop propagation to product card click
        }

        // Check for product card click (less specific).
        // Find the closest element with 'data-action="productcard-click"'.
        const productCard = target.closest<HTMLElement>('.product-card[data-action="productcard-click"]');
        if (productCard) {
          this.onClick(event);
        }
      });
    }

    public onShowSponsorInfoDialog = async (e: Event) => {
      e.preventDefault();
      e.stopPropagation(); // Prevent the event from bubbling to product card

      const element: HTMLElement = e.target as HTMLElement;
      const sponsorElement: HTMLElement = element.closest(
        '[data-action="show-sponsor-info"]'
      ) as HTMLElement;

      const dialogId = 'sponsor-info-dialog';

      this.sponsoredDialogHead = sponsorElement.getAttribute('data-dialog-head');
      this.sponsoredInfoText = sponsorElement.getAttribute('data-info-text');
      this.sponsoredReadMoreButtonText = sponsorElement.getAttribute('data-readmore-button-text');
      this.sponsoredReadMoreButtonLink = sponsorElement.getAttribute('data-readmore-button-link');
      this.sponsoredProductButtonText = sponsorElement.getAttribute('data-product-button-text');
      this.sponsoredProductButtonLink = sponsorElement.getAttribute('data-product-button-link');

      // Ensure dialog exists before opening
      await this.ensureSponsorInfoDialog(dialogId);

      const dialogManager = getDialogManager();
      dialogManager.open('sponsor-info-dialog');

      // Example: you might have data attributes on the element
      // const { sponsorId } = sponsorElement.dataset;
      // ... handle showing sponsor info
    };

    public ensureSponsorInfoDialog = async (dialogId: string): Promise<void> => {

      // Check if dialog already exists in the DOM.
      if (document.getElementById(dialogId)) {
        return;
      }

        // Create the dialog template
      const template = document.createElement('template');
      template.innerHTML = `
        <dialog id="${dialogId}" class="dialog--theme-pink">
        <div class="dialog-content sponsor-info-dialog-content">
          <div class="dialog-header">
            <h4>${this.sponsoredDialogHead}</h4>
            <button data-dialog-close aria-label="Close">
              <i class="icon icon-small icon-remove-white"></i>
            </button>
          </div>
          <div id="sponsored-read-more" class="dialog-body">
            ${this.sponsoredInfoText}
          </div>
          <div class="dialog-footer">
            <a id="dialog-sponsored-readmore" class="button" href="${this.sponsoredReadMoreButtonLink}">${this.sponsoredReadMoreButtonText}</a>
            <a id="dialog-sponsored-product" class="button" href="${this.sponsoredProductButtonLink}">${this.sponsoredProductButtonText}</a>
          </div>
        </div>
        </dialog>
      `;

      // Get the dialog element from the template content (which is a DocumentFragment)
      const dialog = template.content.firstElementChild as HTMLDialogElement;

      // Remove first button if no link
      if (!this.sponsoredReadMoreButtonLink || this.sponsoredReadMoreButtonLink === '') {
        dialog.querySelector('#dialog-sponsored-readmore')?.remove();
      }

      // Remove second button if no link
      if (!this.sponsoredProductButtonLink || this.sponsoredProductButtonLink === '') {
        dialog.querySelector('#dialog-sponsored-product')?.remove();
      }

      // Append to document body
      document.body.appendChild(dialog);

      // Register the new dialog with DialogManager
      const dialogManager = getDialogManager();
      dialogManager.register(dialog);
    };

    public onClick = async (e: Event) => {
      e.preventDefault();

      const element: HTMLElement = e.target as HTMLElement;

      const productCard: HTMLElement = element.closest(
        '.product-card[data-href]'
      ) as HTMLElement;
      const productList: HTMLElement = element.closest(
        '[data-list-name]'
      ) as HTMLElement;
      const sponsorship: HTMLElement = productCard.querySelector(
        '[data-sponsorship-id]'
      ) as HTMLElement;

      const { href } = productCard.dataset;
      if (href && href.trim() !== '') {

        // set referrer list-cookies
        if (productList) {
          const { listName } = productList.dataset;
          const { productEventId } = productCard.dataset;

          if (listName && listName.trim() !== '' && productEventId && productEventId.trim() !== '') {
            localStorage.setItem(
              'referred_list_name',
              listName
            );
            localStorage.setItem(
              'referred_product_sku',
              productEventId
            );
          }
          if (sponsorship && sponsorship.dataset) {
            const { sponsorshipId } = sponsorship.dataset;
            localStorage.setItem(
              'referred_sponsorship_id',
              sponsorshipId
            );
          }
        }
        location.href = href;
      }
    };
  }
}
